/**
 * Shared state for the opening-details page.
 *
 * Two hooks:
 *  - `useOpeningContext()` — non-suspending. Returns local UI state and the
 *    URL `hiringGoalId`. Sibling sections use this for query keys so their
 *    fetches fire in parallel with (not after) the funnel-details API.
 *  - `useOpeningDetails()` — suspending. Performs `useSuspenseQuery` for the
 *    funnel-details API and exposes `opening`, `hiringGoal`, `analyticsData`.
 *    Each consumer suspends at its own nearest <Suspense> boundary, so
 *    sibling sections can render their own skeletons during the initial load
 *    instead of the whole page going blank.
 *
 * Pref toggles that participate in the funnel query key
 * (`showAggregateHires`/`showAggregateSpend`) should trigger updates through
 * `AggregateDataCheckbox`, which wraps changes in `startTransition` locally
 * so the funnel area does not flash back to skeletons while queries refetch.
 */

import { useSuspenseQuery } from '@tanstack/react-query';
import {
  SourcingHiringGoalWithMetrics,
  SourcingOpeningDetailAnalyticsData,
  SourcingService,
  SourcingSetting,
  SourcingStatus,
} from 'api-clients/monolith';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useHireUrl } from '@/hooks/use-hire-url';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

type OpeningSlice = {
  id: string;
  title: string;
  /** URL to edit the opening in the recruiter UI. */
  url: string;
  brandSlug: string | null;
  locationId: string;
  isActive: boolean;
  isSourcingEnabled: boolean;
  sourcingStatus: SourcingStatus;
  automation: SourcingSetting | null;
  hasActiveHiringGoal: boolean;
};

/**
 * The API hiring-goal payload with a recruiter-UI `url` for the opening's
 * hiring-goals tab. `null` when the opening has no goal at all.
 */
type HiringGoalSlice = (SourcingHiringGoalWithMetrics & { url: string }) | null;

type DisplayPrefsState = {
  showAggregateData: boolean;
  showAggregateHires: boolean;
  showAggregateSpend: boolean;
  /** Toggled true by sibling queries that discover linked openings. */
  showAggregateHiresCheckbox: boolean;
};

type DisplayPrefsSlice = DisplayPrefsState & {
  setDisplayPrefs: (partial: Partial<DisplayPrefsState>) => void;
};

type OpeningContextValue = {
  displayPrefs: DisplayPrefsSlice;
  budgetAutomationBannerColor?: 'blue' | 'rose';
  setBudgetAutomationBannerColor: (color?: 'blue' | 'rose') => void;
};

type OpeningContextType = OpeningContextValue & {
  /**
   * Hiring-goal id from the URL `?hiring_goal_id` param. Only present when a
   * past hiring goal is selected; `undefined` for the current goal so the
   * backend can resolve it. Use this in sibling query keys instead of the
   * funnel-derived id so those fetches don't wait on the funnel API.
   */
  hiringGoalId: string | undefined;
};

type OpeningDetailsType = {
  opening: OpeningSlice;
  hiringGoal: HiringGoalSlice;
  analyticsData: SourcingOpeningDetailAnalyticsData;
};

const OpeningContext = createContext<OpeningContextValue | undefined>(
  undefined,
);

/**
 * Holds local UI state for the opening-details page. Does NOT fetch data
 * itself — `useOpeningDetails()` does that lazily via `useSuspenseQuery`,
 * so each consumer suspends against its own nearest Suspense boundary.
 */
export function OpeningProvider({ children }: { children: React.ReactNode }) {
  const [displayPrefsState, setDisplayPrefsState] = useState<DisplayPrefsState>(
    {
      showAggregateData: true,
      showAggregateHires: true,
      showAggregateSpend: false,
      showAggregateHiresCheckbox: false,
    },
  );

  const setDisplayPrefs = useCallback(
    (partial: Partial<DisplayPrefsState>) =>
      setDisplayPrefsState(prev => ({ ...prev, ...partial })),
    [],
  );

  const [budgetAutomationBannerColor, setBudgetAutomationBannerColor] =
    useState<'blue' | 'rose'>();

  const value = useMemo<OpeningContextValue>(
    () => ({
      displayPrefs: { ...displayPrefsState, setDisplayPrefs },
      budgetAutomationBannerColor,
      setBudgetAutomationBannerColor,
    }),
    [displayPrefsState, setDisplayPrefs, budgetAutomationBannerColor],
  );

  return (
    <OpeningContext.Provider value={value}>{children}</OpeningContext.Provider>
  );
}

/**
 * Non-suspending hook for local UI state and the URL-derived hiring-goal id.
 * Safe to call from sibling-query consumers that need a stable query key
 * before the funnel-details API resolves.
 */
export function useOpeningContext(): OpeningContextType {
  const context = useContext(OpeningContext);
  if (!context) {
    throw new Error('useOpeningContext must be used within an OpeningProvider');
  }

  const { hiringGoalId } = useHiringGoalIdParam();

  return useMemo(
    () => ({
      hiringGoalId,
      displayPrefs: context.displayPrefs,
      budgetAutomationBannerColor: context.budgetAutomationBannerColor,
      setBudgetAutomationBannerColor: context.setBudgetAutomationBannerColor,
    }),
    [hiringGoalId, context],
  );
}

/**
 * Suspending hook for funnel-details data (opening, hiring goal, analytics).
 * Consumers that call this must be wrapped in a <Suspense> boundary; React
 * Query dedupes the fetch across sibling consumers.
 */
export function useOpeningDetails(): OpeningDetailsType {
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, displayPrefs } = useOpeningContext();
  const { getHireATHiringGoalUrl, getHireOpeningEditUrl } = useHireUrl();
  const { showAggregateHires, showAggregateSpend } = displayPrefs;

  const { data: funnel } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.detail,
      openingSlug,
      hiringGoalId,
      showAggregateHires,
      showAggregateSpend,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpenings(
        openingSlug,
        hiringGoalId,
        showAggregateHires,
        showAggregateSpend,
      ),
  });

  return useMemo(
    () => ({
      opening: {
        id: funnel.opening_id,
        title: funnel.title,
        url: getHireOpeningEditUrl(funnel.opening_id),
        brandSlug: funnel.brand_slug,
        locationId: funnel.location_id,
        isActive: funnel.active,
        isSourcingEnabled: funnel.is_sourcing_funnel,
        sourcingStatus: funnel.sourcing_status,
        automation: funnel.automation ?? null,
        hasActiveHiringGoal: funnel.has_active_hiring_goal,
      },
      hiringGoal: funnel.hiring_goal && {
        ...funnel.hiring_goal,
        url: getHireATHiringGoalUrl(
          funnel.brand_slug ?? 'no_brand',
          funnel.location_id,
          funnel.opening_id,
        ),
      },
      analyticsData: funnel.analytics,
    }),
    [funnel, getHireATHiringGoalUrl, getHireOpeningEditUrl],
  );
}
